import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ApiError } from "../api/client";
import {
  fetchBadges,
  fetchBatches,
  formatBatchLabel,
  type BadgeRecord,
  type BatchSummary,
} from "../api/kiosk";
import AdminLayout from "../components/AdminLayout";
import { useAdminBranch } from "../context/AdminBranchContext";
import { useStaff } from "../context/StaffContext";
import "./BadgePrintScreen.css";

const DEFAULT_FILTERS = { active: "Active", day: "", program: "", delivery: "" };

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** `batch.day` can be comma-separated ("Monday,Wednesday"). */
function batchDays(batch: BatchSummary): string[] {
  return (batch.day || "")
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean);
}

export default function BadgePrintScreen() {
  return (
    <AdminLayout
      branchPicker
      heading={
        <>
          Print student <span className="accent">badges</span>
        </>
      }
      intro="Sign in with your portal account to fetch badge codes. The session stays on this tab and signs itself out after 30 minutes idle."
    >
      <BadgePrinterForBranch />
    </AdminLayout>
  );
}

/**
 * Keying on the header's branch remounts the printer whenever it changes, so
 * batches, badges, ticks and filters all reset to a clean page — no effect
 * choreography.
 */
function BadgePrinterForBranch() {
  const { branchId } = useAdminBranch();
  return <BadgePrinter key={String(branchId)} />;
}

/**
 * Split from the gate so it only ever mounts with a live staff token — no
 * `if (!token)` branch threaded through every handler.
 */
function BadgePrinter() {
  const { staff, handleApiError } = useStaff();
  const token = staff?.token ?? "";
  // Which branch we act for is a header concern now — shared, persisted, and
  // pre-answered by the device's registration for most staff.
  const { branchId, error: branchError } = useAdminBranch();

  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [batchId, setBatchId] = useState<number | "">("");
  const [badges, setBadges] = useState<BadgeRecord[]>([]);
  // user_ids included in the print sheet. Every fetch selects everyone — the
  // common case is the whole class; narrowing is the exception, not a step.
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mirrors the portal's class-list filters: client-side over the one fetch,
  // Active by default, auto-applied on change (BatchFilter.js over there).
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  // When set, the print sheet is exactly this one student, whatever the ticks
  // say — the per-card Print button. Cleared as soon as the dialog closes.
  const [soloPrintId, setSoloPrintId] = useState<number | null>(null);

  // The class list for the header's branch. Branch changes remount this whole
  // component (see BadgePrinterForBranch), so this is a load-on-mount.
  useEffect(() => {
    if (branchId === "" || !token) return;
    let cancelled = false;
    void (async () => {
      setBusy(true);
      try {
        const rows = await fetchBatches(token, branchId);
        if (!cancelled) setBatches(rows);
      } catch (caught) {
        if (cancelled) return;
        if (handleApiError(caught)) return;
        setError(describe(caught, "Could not load classes for that branch."));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, handleApiError, token]);

  // Print one card: mark it, let the class change paint, open the dialog, and
  // put the sheet back however it closes.
  useEffect(() => {
    if (soloPrintId === null) return;
    const finish = () => setSoloPrintId(null);
    window.addEventListener("afterprint", finish);
    const timer = window.setTimeout(() => {
      window.print(); // blocks until the dialog closes on desktop browsers
      finish();
    }, 50);
    return () => {
      window.removeEventListener("afterprint", finish);
      window.clearTimeout(timer);
    };
  }, [soloPrintId]);

  const toggleSelected = useCallback((userId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  /* Dropdown options come from the batches themselves, so each filter only
     ever offers values that exist at this branch. */
  const filterOptions = useMemo(() => {
    const days = new Set<string>();
    const programs = new Set<string>();
    const deliveries = new Set<string>();
    for (const batch of batches) {
      for (const day of batchDays(batch)) days.add(day);
      if (batch.program_name) programs.add(batch.program_name);
      if (batch.delivery_type_value) deliveries.add(batch.delivery_type_value);
    }
    return {
      days: [...days].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b)),
      programs: [...programs].sort(),
      deliveries: [...deliveries].sort(),
    };
  }, [batches]);

  const filteredBatches = useMemo(() => {
    return batches.filter(
      (batch) =>
        (filters.active === "" || (batch.active_value ?? "") === filters.active) &&
        // Day matches any of a multi-day batch's days — the portal's strict
        // equality misses "Monday,Wednesday"; we don't repeat that.
        (filters.day === "" || batchDays(batch).includes(filters.day)) &&
        (filters.program === "" || batch.program_name === filters.program) &&
        (filters.delivery === "" || batch.delivery_type_value === filters.delivery),
    );
  }, [batches, filters]);

  // A filter that hides the chosen class suspends the choice (and the ticks
  // over its fetched sheet); widening the filter brings both straight back. A
  // manually typed batch id (the fallback path) is never in the list at all,
  // so filters leave it alone.
  const effectiveBatchId = useMemo(() => {
    if (batchId === "") return "" as const;
    const known = batches.some((batch) => batch.batch_id === batchId);
    const visible = filteredBatches.some((batch) => batch.batch_id === batchId);
    return known && !visible ? ("" as const) : batchId;
  }, [batchId, batches, filteredBatches]);

  const handleFetchBadges = useCallback(async () => {
    if (effectiveBatchId === "") return;
    setBusy(true);
    setError(null);
    setBadges([]);
    setSelected(new Set());
    try {
      const rows = await fetchBadges(token, { batch_id: effectiveBatchId });
      setBadges(rows);
      setSelected(new Set(rows.map((row) => row.user_id)));
      if (rows.length === 0) {
        // Badges are auto-issued for every enrolled student, so an empty
        // result always means an empty roster — never "not printed yet".
        setError("No students are enrolled in that class.");
      }
    } catch (caught) {
      if (handleApiError(caught)) return;
      setError(describe(caught, "Could not fetch badges for that class."));
    } finally {
      setBusy(false);
    }
  }, [effectiveBatchId, handleApiError, token]);

  /*
    Active classes first, then by day and start time. A branch can carry years
    of retired batches, and a picker that opens on those is a picker nobody
    trusts.
  */
  const batchOptions = useMemo(() => {
    return [...filteredBatches]
      .sort((a, b) => {
        const activeA = a.active_value === "Active" ? 0 : 1;
        const activeB = b.active_value === "Active" ? 0 : 1;
        if (activeA !== activeB) return activeA - activeB;
        return (a.start_time || "").localeCompare(b.start_time || "");
      })
      .map((batch) => {
        // Twin slots (same day + time) render identically otherwise, and the
        // count exposes empty shell batches before anyone fetches them.
        const count =
          typeof batch.enrolled_student_count === "number"
            ? `  ·  ${batch.enrolled_student_count} student${
                batch.enrolled_student_count === 1 ? "" : "s"
              }`
            : "";
        return {
          id: batch.batch_id,
          label: `${formatBatchLabel(batch)}${count}${
            batch.active_value === "Active" ? "" : "  (inactive)"
          }`,
        };
      });
  }, [filteredBatches]);

  return (
    <main className="badges-stage">
      <div className="badges-inner">
        <div>
          <p className="eyebrow badges-eyebrow">Staff tool</p>
          <h1 className="badges-heading">
            Print student <span className="accent">badges</span>
          </h1>
        </div>

        {/* Same shape as the portal's class-list filter bar: native selects,
            an "All" option each, auto-applied, "Filter by …" captions. */}
        <div className="filter-row no-print">
          <div className="field">
            <select
              id="filter-active"
              aria-label="Filter by Active"
              value={filters.active}
              disabled={batches.length === 0}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, active: event.target.value }))
              }
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="In-Active">In-Active</option>
            </select>
            <small className="filter-caption">
              <b>Filter</b> by Active
            </small>
          </div>
          <div className="field">
            <select
              id="filter-day"
              aria-label="Filter by Day"
              value={filters.day}
              disabled={batches.length === 0}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, day: event.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions.days.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <small className="filter-caption">
              <b>Filter</b> by Day
            </small>
          </div>
          <div className="field">
            <select
              id="filter-program"
              aria-label="Filter by Program"
              value={filters.program}
              disabled={batches.length === 0}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, program: event.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions.programs.map((program) => (
                <option key={program} value={program}>
                  {program}
                </option>
              ))}
            </select>
            <small className="filter-caption">
              <b>Filter</b> by Program
            </small>
          </div>
          <div className="field">
            <select
              id="filter-delivery"
              aria-label="Filter by Delivery Type"
              value={filters.delivery}
              disabled={batches.length === 0}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, delivery: event.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions.deliveries.map((delivery) => (
                <option key={delivery} value={delivery}>
                  {delivery}
                </option>
              ))}
            </select>
            <small className="filter-caption">
              <b>Filter</b> by Delivery Type
            </small>
          </div>
        </div>

        <div className="picker-row">
          <div className="field">
            <label htmlFor="badge-batch">Class</label>
            <select
              id="badge-batch"
              value={effectiveBatchId}
              disabled={batchOptions.length === 0}
              onChange={(event) =>
                setBatchId(event.target.value === "" ? "" : Number(event.target.value))
              }
            >
              <option value="">
                {branchId === ""
                  ? "Pick a branch in the top bar…"
                  : batches.length === 0
                    ? busy
                      ? "Loading classes…"
                      : "No classes at this branch"
                    : batchOptions.length === 0
                      ? "No classes match the filters…"
                      : "Choose a class…"}
              </option>
              {batchOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/*
          Fallback path from the contract: if the class list is empty or
          the label is unrecognisable, staff can type the batch id from
          the portal URL directly.
        */}
        <details className="hint no-print">
          <summary style={{ cursor: "pointer" }}>
            Class not listed? Enter a batch ID manually
          </summary>
          <div className="field" style={{ maxWidth: "16rem", marginTop: "0.75rem" }}>
            <label htmlFor="badge-batch-manual">Batch ID</label>
            <input
              id="badge-batch-manual"
              type="number"
              inputMode="numeric"
              min={1}
              value={batchId}
              onChange={(event) =>
                setBatchId(event.target.value === "" ? "" : Number(event.target.value))
              }
            />
          </div>
        </details>

        {(branchError || error) && (
          <p className="form-error" role="alert">
            {branchError || error}
          </p>
        )}

        <div className="badges-toolbar">
          <button
            type="button"
            className="btn"
            disabled={busy || effectiveBatchId === ""}
            onClick={() => void handleFetchBadges()}
          >
            {busy ? "Loading…" : "Fetch badges"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={() => window.print()}
          >
            Print sheet
          </button>
          {badges.length > 0 && (
            <span className="select-group">
              <button
                type="button"
                className="btn btn-small"
                disabled={selected.size === badges.length}
                onClick={() => setSelected(new Set(badges.map((b) => b.user_id)))}
              >
                All
              </button>
              <button
                type="button"
                className="btn btn-small"
                disabled={selected.size === 0}
                onClick={() => setSelected(new Set())}
              >
                None
              </button>
            </span>
          )}
          <span className="spacer" />
          <span className="badges-count">
            {badges.length === 0
              ? "0 badges"
              : `${selected.size} of ${badges.length} selected`}
          </span>
        </div>

        {badges.length > 0 ? (
          <>
            <p className="hint no-print">
              Every student starts ticked — click a card to leave it off the sheet. Print
              at 100% scale — turn off &ldquo;fit to page&rdquo;. Card stock or lamination
              roughly triples how long a badge survives a schoolbag.
            </p>
            <div className="sheet">
              {badges.map((badge) => (
                <BadgeCard
                  key={badge.badge_code}
                  badge={badge}
                  selected={selected.has(badge.user_id)}
                  printSkipped={
                    soloPrintId !== null
                      ? badge.user_id !== soloPrintId
                      : !selected.has(badge.user_id)
                  }
                  onToggle={() => toggleSelected(badge.user_id)}
                  onPrintOne={() => setSoloPrintId(badge.user_id)}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="badges-empty">
            Pick a class and fetch badges to build a print sheet.
          </p>
        )}
      </div>
    </main>
  );
}

function BadgeCard({
  badge,
  selected,
  printSkipped,
  onToggle,
  onPrintOne,
}: {
  badge: BadgeRecord;
  selected: boolean;
  /** Left off the printed sheet — from the ticks, or from a solo print. */
  printSkipped: boolean;
  onToggle: () => void;
  onPrintOne: () => void;
}) {
  const name = `${badge.firstname ?? ""} ${badge.lastname ?? ""}`.trim();
  return (
    // A label so the whole card toggles its checkbox — the native control
    // carries the state; skipped cards drop out of the sheet at print.
    <label
      className={`badge-card${selected ? "" : " badge-card--off"}${
        printSkipped ? " badge-card--skip" : ""
      }`}
    >
      <input
        type="checkbox"
        className="badge-pick no-print"
        checked={selected}
        onChange={onToggle}
        aria-label={`Include ${name || "unnamed student"} on the print sheet`}
      />
      <button
        type="button"
        className="badge-print-one no-print"
        onClick={(event) => {
          // Inside a label: don't let the click double as a tick toggle.
          event.preventDefault();
          event.stopPropagation();
          onPrintOne();
        }}
      >
        Print this one
      </button>
      <div className="badge-qr">
        {/*
          The QR encodes the bare badge code and nothing else — no URL, no
          payload. High error correction so a scuffed card still reads
          (docs/decisions.md D-003).
        */}
        <QRCodeSVG
          value={badge.badge_code}
          size={256}
          level="H"
          marginSize={2}
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </div>
      <div className="badge-body">
        <p className="badge-name">{name || "Unnamed student"}</p>
        <p className="badge-code">{badge.badge_code}</p>
        {/*
          The mono asset, not the brand-blue one, on screen as well as on
          paper: the card is a preview of the printed badge, so it should
          show what comes out of the printer.
        */}
        <img
          className="badge-brand"
          src="/brand/logo-horizontal-black.png"
          alt="Zebra Robotics"
        />
      </div>
    </label>
  );
}

function describe(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    if (caught.isNetwork) return "Could not reach the server. Check the network.";
    return caught.message || fallback;
  }
  return fallback;
}
